export const COMPANY_STORAGE_KEY = "meucontroleai:company"

/**
 * Shape of the merchant/company payload persisted after
 * `GET /by-subdomain/:subdomain`. Production currently returns
 * `{ id, accessState, domains: { subdomain } }` — subdomain is nested.
 */
export type Company = {
  id: string
  subdomain?: string
  slug?: string
  document?: string
  name?: string
  accessState?: "ACTIVE" | "SUSPENDED"
  domains?:
    | { subdomain?: string; slug?: string; domainType?: string }
    | Array<{ subdomain?: string; slug?: string; domainType?: string }>
} & Record<string, unknown>

function hasNonEmptyId(value: unknown): value is { id: string } {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { id?: unknown }).id === "string" &&
    (value as { id: string }).id.length > 0
  )
}

function domainSubdomains(company: Company): string[] {
  const domains = company.domains
  if (!domains) return []
  if (Array.isArray(domains)) {
    return domains
      .map((d) => d.subdomain || d.slug)
      .filter((v): v is string => typeof v === "string" && v.length > 0)
  }
  const single = domains.subdomain || domains.slug
  return typeof single === "string" && single.length > 0 ? [single] : []
}

/**
 * True when the stored company belongs to the storefront host subdomain.
 * Accepts top-level `subdomain`/`slug` or nested `domains.subdomain` (API shape).
 */
export function companyBelongsToSubdomain(
  company: Company,
  subdomain: string,
): boolean {
  if (!subdomain) return false
  if (company.subdomain === subdomain || company.slug === subdomain) {
    return true
  }
  return domainSubdomains(company).includes(subdomain)
}

/**
 * Returns true when `value` is safe to persist as the storefront company.
 */
export function isPersistableCompany(value: unknown): value is Company {
  return hasNonEmptyId(value)
}

/**
 * Stamps a top-level `subdomain` so cache matching survives API payloads that
 * only nest it under `domains`.
 */
export function normalizeCompanyPayload(
  data: Company,
  lookupSubdomain?: string,
): Company {
  const nested = domainSubdomains(data)[0]
  const subdomain = data.subdomain || data.slug || nested || lookupSubdomain
  if (!subdomain || data.subdomain === subdomain) {
    return data
  }
  return { ...data, subdomain }
}

/**
 * Reads the stored company from localStorage.
 * Returns null when absent or corrupted (never throws).
 */
export function getStoredCompany(): Company | null {
  try {
    const raw = localStorage.getItem(COMPANY_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return isPersistableCompany(parsed) ? parsed : null
  } catch {
    return null
  }
}

/**
 * Returns the cached company only when it belongs to the current subdomain.
 */
export function getCachedCompanyForSubdomain(subdomain: string): Company | null {
  const company = getStoredCompany()
  if (!company) return null
  return companyBelongsToSubdomain(company, subdomain) ? company : null
}

/**
 * Persists a by-subdomain payload only when it carries a non-empty merchant id.
 * Never writes `null` / empty id. Optionally stamps `subdomain` from the lookup.
 */
export function persistCompanyIfValid(
  data: unknown,
  lookupSubdomain?: string,
): Company | null {
  if (!isPersistableCompany(data)) {
    return null
  }
  const normalized = normalizeCompanyPayload(data, lookupSubdomain)
  try {
    localStorage.setItem(COMPANY_STORAGE_KEY, JSON.stringify(normalized))
    return normalized
  } catch {
    return null
  }
}

/**
 * Removes the stored company entry.
 */
export function clearStoredCompany(): void {
  try {
    localStorage.removeItem(COMPANY_STORAGE_KEY)
  } catch {
    // ignore storage failures
  }
}

/**
 * Returns the merchant id from the stored company, or null when
 * the company is absent, corrupted or has no id.
 */
export function getMerchantId(): string | null {
  return getStoredCompany()?.id ?? null
}

/**
 * Returns the merchant id or throws when unavailable.
 * Use in flows that cannot proceed without a merchant (e.g. checkout).
 */
export function getMerchantIdOrThrow(): string {
  const merchantId = getMerchantId()
  if (!merchantId) {
    throw new Error("ID do comerciante não encontrado")
  }
  return merchantId
}
